import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow, format } from 'date-fns';
import { enGB as en, uk } from 'date-fns/locale';
import { ReactComponent as ViewsIcon } from '../../assets/views.svg';
import { ReactComponent as EngagementIcon } from '../../assets/engagement.svg';
import { ReactComponent as SimilarityIcon } from '../../assets/similarity.svg';
import { ReactComponent as SettingsIcon } from '../../assets/settings.svg';
import { ReactComponent as UpdateIcon } from '../../assets/update.svg';
import { ReactComponent as SharesIcon } from './assets/repost-clear.svg';
import { ReactComponent as CommentsIcon } from './assets/comment-clear.svg';
import { ReactComponent as ReactionsIcon } from '../../assets/thumbUp.svg';
import { formatWithCustomGigaBillion } from '../../utils/fomatNumbers';
import { Sentiment } from '../Sentiment/Sentiment';
import { DropdownMenu } from '../DropdownMenu/DropdownMenu';
import { ButtonsList } from '../DropdownMenu/ButtonsList';
import { metricSupport } from '../../utils/metrics';
import { useEmbeddedWindmillApps } from '../WindmillEmbedded/useEmbeddedWindmillApps';
import { EmbeddedAppRunModal } from '../WindmillEmbedded/EmbeddedAppRunModal';
import { messageEmbeddingContext } from '../WindmillEmbedded/messageEmbeddingContext';
import styles from './ExtendedMessageView.module.scss';

function isMetricSupported(sourceType, metric, contentType) {
  return metricSupport[metric]?.[sourceType]?.[contentType] ?? false;
}

export function MessageMetricsWrapper({
  message,
  narrative,
  isShare,
  isMessageDropdownOpen,
  setIsMessageDropdownOpen,
  messageMenuOptions,
}) {
  const { t } = useTranslation();
  const sourceType = message?.source?.source_type;

  // Windmill apps embedded in message cards. Only where the card has an
  // interactive menu (not in PDF exports, share views or read-only lists).
  const withAppsMenu = !isShare && Boolean(setIsMessageDropdownOpen);
  const windmillApps = useEmbeddedWindmillApps('message', withAppsMenu);
  const [activeWindmillApp, setActiveWindmillApp] = useState(null);
  const windmillEmbedding = useMemo(
    () => messageEmbeddingContext(message, narrative),
    [message, narrative],
  );
  const WINDMILL_PREFIX = 'windmill:';
  const menuOptions = [
    ...(messageMenuOptions || []),
    ...windmillApps.map((app) => ({
      name: app.icon ? `${app.icon} ${app.name}` : app.name,
      value: `${WINDMILL_PREFIX}${app.id}`,
    })),
  ];

  const getUserLocale = () => {
    const lang = localStorage.getItem('i18nextLng');
    if (lang === 'ua-UK' || lang === 'ru-RU' || lang === 'ru' || lang === 'uk') {
      return uk;
    }
    return en;
  };

  const locale = getUserLocale();

  const parseUTCDate = (dateString) => {
    if (!dateString) return null;
    const utcString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    const date = new Date(utcString);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const isMoreThan24HoursAgo = (dateString) => {
    if (!dateString) return false;
    const date = parseUTCDate(dateString);
    if (!date) return false;
    return (new Date().getTime() - date.getTime()) / (1000 * 60 * 60) > 24;
  };

  const messageDateRaw = message?.date_modified || null;

  const showMessageTime = () => {
    if (!messageDateRaw) return null;
    const date = parseUTCDate(messageDateRaw);
    if (!date) return null;
    if (isMoreThan24HoursAgo(messageDateRaw)) {
      return t('Updated: {{date}} at {{time}}', {
        date: format(date, 'dd-MM-yyyy', { locale }),
        time: format(date, 'HH:mm', { locale }),
      });
    }
    return t('Updated: {{time}} ago', {
      time: formatDistanceToNow(date, { locale }),
    });
  };

  const messageTimeString = showMessageTime();

  const allMetricsNA =
    !isMetricSupported(sourceType, 'views', message.content_type) &&
    !isMetricSupported(sourceType, 'comments', message.content_type) &&
    !isMetricSupported(sourceType, 'reposts', message.content_type) &&
    !isMetricSupported(sourceType, 'reactions', message.content_type) &&
    !isMetricSupported(sourceType, 'engagement', message.content_type);

  const additionalTooltipInfo = (metric) => {
    const contentType = message?.content_type;
    let isMetricSupportedOnPlatform = isMetricSupported(sourceType, metric, contentType);
    if (isMetricSupportedOnPlatform === false) {
      return t("Platform doesn't provide this metric");
    } else if (isMetricSupportedOnPlatform === null) {
      return t('Metric not collected for this platform');
    }
    if (sourceType === 'FACEBOOK' && metric === 'comments' && contentType === 'POST') {
      return t('Facebook updates values with a delay, so the metrics can include comments that were already deleted, banned, or hidden. Also, Osavul filters out comments under 3 words.');
    } else {
      return t('Metric may update with a delay');
    }
  };

  return (
    <div className={styles.messageMetricsWrapper}>
      <div className={styles.messageMetrics}>
        {narrative && message.sentiment_score !== null ? (
          <Sentiment
            sentimentScore={message.sentiment_score}
            messageId={message.id}
            narrative={narrative}
            isShare={isShare}
          />
        ) : (
          ''
        )}
        <span
          className={styles.anchor}
          data-tooltip-id="message-views"
          data-tooltip-content={`${t('Views')}\n ${additionalTooltipInfo('views')}`}
        >
          <ViewsIcon />
          {isMetricSupported(sourceType, 'views', message.content_type)
            ? formatWithCustomGigaBillion(message.impressions ?? message.views ?? 0)
            : t('N/A')}
        </span>
        <span
          className={styles.anchor}
          data-tooltip-id="message-comments"
          data-tooltip-content={`${t('Comments')}\n ${additionalTooltipInfo('comments')}`}
        >
          <CommentsIcon />
          {isMetricSupported(sourceType, 'comments', message.content_type)
            ? formatWithCustomGigaBillion((message.replies || message.comments) || 0)
            : t('N/A')}
        </span>
        <span
          className={styles.anchor}
          data-tooltip-id="message-reposts"
          data-tooltip-content={`${t('Reposts')}\n ${additionalTooltipInfo('reposts')}`}
        >
          <SharesIcon />
          {isMetricSupported(sourceType, 'reposts', message.content_type)
            ? formatWithCustomGigaBillion(message.forwards || message.shares || 0)
            : t('N/A')}
        </span>
        <span
          className={styles.anchor}
          data-tooltip-id="message-reactions"
          data-tooltip-content={`${t('Reactions')}\n ${additionalTooltipInfo('reactions')}`}
        >
          <ReactionsIcon style={{width: '23px', height: '23px'}}/>
          {isMetricSupported(sourceType, 'reactions', message.content_type)
            ? formatWithCustomGigaBillion(message.reactions ?? 0)
            : t('N/A')}
        </span>
        <span
          className={styles.anchor}
          data-tooltip-id="message-engagement"
          data-tooltip-content={`${t('Engagement')}\n ${t('Total amount of comments, reposts and reactions')}`}
        >
          <EngagementIcon/>
          {isMetricSupported(sourceType, 'engagement', message.content_type)
            ? message.engagement
              ? formatWithCustomGigaBillion(message.engagement)
              : formatWithCustomGigaBillion(
                  parseInt(message.forwards ?? 0) +
                    parseInt(message.reactions ?? 0) +
                    parseInt(message.replies ?? 0),
                )
            : t('N/A')}{/* last condition for search page metrics */}
        </span>
        {message.similarity_score ? (
          <span
            className={styles.anchor}
            data-tooltip-id="message-similarity"
            data-tooltip-content={t('Similarity1')}
          >
            <SimilarityIcon />
            {(message.similarity_score + '').slice(0, 4)}
          </span>
        ) : (
          ''
        )}
        {messageTimeString && !allMetricsNA ? (
          <span className={styles.messageUpdateTime}>
            <UpdateIcon className={styles.updateIcon} aria-hidden="true" />
            {messageTimeString}
          </span>
        ) : null}
      </div>
      {menuOptions.length > 0 ? (
        <div className={styles.cardControls}>
          <DropdownMenu
            icon={<SettingsIcon />}
            isOpen={isMessageDropdownOpen[message.id]}
            onClick={() => {
              setIsMessageDropdownOpen({
                ...isMessageDropdownOpen,
                [message.id]: !isMessageDropdownOpen[message.id],
              });
            }}
            menuClassName="messageMenu"
            key={`dropdown-${message.id}`}
          >
            <ButtonsList
              itemsList={menuOptions}
              onClick={(value) => {
                if (value.startsWith(WINDMILL_PREFIX)) {
                  const appId = value.slice(WINDMILL_PREFIX.length);
                  setIsMessageDropdownOpen({
                    ...isMessageDropdownOpen,
                    [message.id]: false,
                  });
                  setActiveWindmillApp(
                    windmillApps.find((app) => app.id === appId) || null,
                  );
                } else if (value === 'comments') {
                  let parentId =
                    message?.parent_message?.composite_id || message?.parent_id;
                  if (message?.content_type === 'POST') {
                    parentId = message?.composite_id || message?.id;
                  }
                  window.open(`/search?comments_to=${parentId}`, '_blank');
                } else if (value === 'comments_to_video') {
                  const compositeId = message?.composite_id || message?.id;
                  window.open(`/search?comments_to=${compositeId}`, '_blank');
                } else if (value === 'reposts_to_post') {
                  const compositeId = message?.composite_id || message?.id;
                  window.open(`/search?reposts_to=${compositeId}`, '_blank');
                } else if (value === 'similar') {
                  window.open(
                    `/search?similar_to=${message?.composite_id}`,
                    '_blank',
                  );
                }
              }}
            />
          </DropdownMenu>
        </div>
      ) : (
        ''
      )}
      {activeWindmillApp && (
        <EmbeddedAppRunModal
          key={activeWindmillApp.id}
          app={activeWindmillApp}
          embedding={windmillEmbedding}
          onClose={() => setActiveWindmillApp(null)}
        />
      )}
    </div>
  );
}
